import { useEffect } from 'react';
import type { MutableRefObject } from 'react';

// Utils
import { colorBorders } from '../utils/domUtils/tableCellBorderUtils';
import type { MNHotInstance } from '../tableHotInstanceTypes';
import { isHotInstanceAvailable } from '../utils/hotInstanceUtils/hotTableInstanceValidationUtils';

/**
 * Custom hook for managing table cell editing side effects.
 * Handles syncing props to Handsontable instance.
 */
export const useTableCellEditingEffects = ({
    locale,
    filterQuery,
    isReadOnly,
    isDarkMode,
    hotTableInstanceRef,
}: {
    locale: string;
    filterQuery?: string;
    isReadOnly: boolean;
    isDarkMode: boolean;
    hotTableInstanceRef: MutableRefObject<MNHotInstance | null>;
}): void => {
    /**
     * Re-render on locale and filterQuery changes.
     */
    useEffect(() => {
        const hotInstance = hotTableInstanceRef.current;
        if (!isHotInstanceAvailable(hotInstance)) return;

        hotInstance.render();
    }, [hotTableInstanceRef, locale, filterQuery]);

    /**
     * If table is readonly, disable HOT shortcuts
     */
    useEffect(() => {
        if (!isReadOnly) return;

        // disable all shortcuts
        hotTableInstanceRef.current?.unlisten();
    }, [hotTableInstanceRef, isReadOnly]);

    /**
     * Update relevant values with new darkmode setting.
     */
    useEffect(() => {
        const hotInstance = hotTableInstanceRef.current;
        if (!hotInstance) return;

        // recalculate the coloured cell borders with the new darkmode setting
        const dataArray = hotInstance.getData();
        for (let row = 0; row < dataArray.length; row++) {
            for (let col = 0; col < dataArray[row].length; col++) {
                colorBorders(hotInstance, row, col);
            }
        }
    }, [hotTableInstanceRef, isDarkMode]);
};

export default useTableCellEditingEffects;
